import { useEffect, useMemo, useState } from 'react';

import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';
import { useController } from 'react-hook-form';
import { Link, Navigate, useParams } from 'react-router';
import { LuCalendarDays, LuClock, LuHash, LuUsers } from 'react-icons/lu';

import { useIdempotencyKey } from '@mds/shared/hooks/use-idempotency-key';
import { useSessionDraft } from '@mds/shared/hooks/use-session-draft';
import { ApiError } from '@mds/shared/lib/api';
import { closedWeekdays, firstOpenDay } from '@mds/shared/lib/booking-slots';
import { clockRange, durationLabel, formatCents, longDateLabel } from '@mds/shared/lib/format';
import { toneIcon } from '@mds/shared/lib/tone';
import type { GuestProposalReceipt, GuestProposalResource } from '@mds/shared/types/proposal';
import type { ResourceOperatingHour, ResourceWeekday } from '@mds/shared/types/resource';
import BookingCalendar, { type Selection } from '@mds/shared/ui/booking-calendar';
import { Alert } from '@mds/shared/ui/feedback/alert';
import { Field } from '@mds/shared/ui/form/field';
import { Form, applyServerErrors, useZodForm } from '@mds/shared/ui/form/form';
import { FormSection } from '@mds/shared/ui/form/form-section';
import { PhoneInput } from '@mds/shared/ui/form/phone-input';
import { Button } from '@mds/shared/ui/shadcn/button';
import { Input } from '@mds/shared/ui/shadcn/input';
import { Textarea } from '@mds/shared/ui/shadcn/textarea';

import PageHeader from '@/components/heading/page-header';
import OutcomePanel from '@/components/section/outcome-panel';
import { EmptyBlock, ErrorBlock, LoadingBlock } from '@/components/states';
import { proposalSchema, type ProposalForm } from '@/features/proposals/proposal.schema';
import * as proposalsApi from '@/features/proposals/proposals.api';
import { proposalQueries } from '@/features/proposals/proposals.queries';
import { spaceQueries } from '@/features/spaces/spaces.queries';
import NotFoundPage from './not-found';

const band = 'mx-auto max-w-page px-gutter pt-section';
/**
 * The form's column, centred on the page with its header: capped rather than
 * page-wide, since short fields across the whole page left each one half a
 * screen long for a name or a phone number. `box-content` keeps the gutter
 * outside the cap, so the column is the same width at every screen size.
 */
const column = 'mx-auto box-content max-w-3xl px-gutter pt-section';

/**
 * The facility's zone. The public API does not send the facility, and MDS is one
 * building in one zone — the backend's own default for it.
 */
const FACILITY_TIMEZONE = 'Asia/Beirut';

/** Today where the facility is, so a visitor abroad cannot pick a past date. */
const todayIn = (timezone: string) =>
  new Intl.DateTimeFormat('en-CA', { timeZone: timezone }).format(new Date());

/** The calendar's errors wear the same mark as a field's. */
const ErrorMark = toneIcon.danger;

const WEEK: ResourceWeekday[] = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
];
const plural = (weekday: ResourceWeekday) => `${weekday[0]!.toUpperCase()}${weekday.slice(1)}s`;

/**
 * "Available for booking on Saturdays only, 2:00 PM – 6:00 PM." from the weekly hours, for
 * a space that opens on only some days; nothing when it opens every day. The
 * hours are added only when every open day shares them.
 */
function openDaysNote(hours: ResourceOperatingHour[]): string | null {
  const open = WEEK.map((weekday) => hours.find((h) => h.weekday === weekday)).filter(
    (h): h is ResourceOperatingHour => !!h && !h.isClosed,
  );
  if (open.length === 0 || open.length === WEEK.length) return null;

  const names = open.map((h) => plural(h.weekday));
  const days =
    names.length === 1 ? names[0] : `${names.slice(0, -1).join(', ')} and ${names.at(-1)}`;
  const first = open[0]!;
  const sameHours =
    first.openMinute !== null &&
    first.closeMinute !== null &&
    open.every((h) => h.openMinute === first.openMinute && h.closeMinute === first.closeMinute);
  return `Available for booking on ${days} only${sameHours ? `, ${clockRange(first.openMinute!, first.closeMinute!)}` : ''}.`;
}

/** Whether every hour from `start` for `length` minutes is free. */
const fits = (free: Set<number>, start: number, length: number) => {
  for (let m = start; m < start + length; m += 60) if (!free.has(m)) return false;
  return true;
};

/** The 409 for a key reused with different details. */
const isReusedKey = (error: unknown) =>
  error instanceof ApiError && error.code === 'PROPOSAL_REQUEST_ID_REUSED';

/**
 * A space configured to use proposal review instead of a direct booking request.
 *
 * These are not requested off an availability board. The guest describes what
 * they want, MDS reviews it by hand, and every step after that reaches them by
 * email.
 */
export default function SpaceProposalPage() {
  const { slug = '' } = useParams();
  const detail = useQuery(spaceQueries.detail(slug));
  const resources = useQuery(proposalQueries.resources());
  const [receipt, setReceipt] = useState<GuestProposalReceipt | null>(null);

  if (detail.isError && detail.error instanceof ApiError && detail.error.status === 404) {
    return <NotFoundPage />;
  }
  if (detail.isPending || detail.isError) {
    return (
      <section className={band}>
        {detail.isPending ? (
          <LoadingBlock />
        ) : (
          <ErrorBlock
            title="We couldn't load this space"
            error={detail.error}
            onRetry={() => void detail.refetch()}
          />
        )}
      </section>
    );
  }
  const space = detail.data;
  // A direct-request space has no proposal form; its own page is the way in.
  if (space.bookingMode !== 'proposal') return <Navigate to={`/spaces/${slug}`} replace />;

  if (receipt) {
    return (
      <div className={band}>
        <Receipt receipt={receipt} />
      </div>
    );
  }

  const resource = resources.data?.find((r) => r.id === space.id);
  const back = { label: space.name, to: `/spaces/${space.slug}` };

  if (resources.isPending || resources.isError || !resource) {
    return (
      <section className={column}>
        <PageHeader back={back} title={`Proposal for ${space.name}`} />
        <div className="mt-block">
          {resources.isPending ? (
            <LoadingBlock />
          ) : resources.isError ? (
            <ErrorBlock
              title="We couldn't load this space"
              error={resources.error}
              onRetry={() => void resources.refetch()}
            />
          ) : (
            <EmptyBlock
              title="This space is not taking proposals right now"
              hint="Contact MDS and we'll help."
            />
          )}
        </div>
      </section>
    );
  }

  const unit = resource.rate.unit === 'block' ? 'booking' : resource.rate.unit;

  return (
    <section className={column}>
      <PageHeader
        back={back}
        title={`Proposal for ${resource.name}`}
        description={[
          'Tell us what you are planning. MDS reviews it and emails you a price.',
          openDaysNote(resource.operatingHours),
        ]
          .filter(Boolean)
          .join(' ')}
        // A block is one price for one stretch, so it reads as a single line
        // ("$120.00 for 4 hours"); an hourly rate keeps its minimum apart.
        meta={
          resource.rate.unit === 'block'
            ? [
                `${formatCents(resource.rate.amountMinor)} for ${durationLabel(resource.minimumBookingMinutes)}`,
                `Up to ${resource.capacity} people`,
              ]
            : [
                `${formatCents(resource.rate.amountMinor)} per ${unit}`,
                `Up to ${resource.capacity} people`,
                `Minimum ${durationLabel(resource.minimumBookingMinutes)}`,
              ]
        }
      />
      <ProposalForm
        resource={resource}
        spaceSlug={space.slug}
        today={todayIn(FACILITY_TIMEZONE)}
        onReceipt={setReceipt}
      />
    </section>
  );
}

function ProposalForm({
  resource,
  spaceSlug,
  today,
  onReceipt,
}: {
  resource: GuestProposalResource;
  spaceSlug: string;
  today: string;
  onReceipt: (receipt: GuestProposalReceipt) => void;
}) {
  const schema = useMemo(() => proposalSchema(resource, today), [resource, today]);

  // The longest public form, so what the visitor typed survives a reload in
  // this tab; cleared once the proposal is filed.
  const [draft, setDraft, clearDraft] = useSessionDraft<ProposalForm>(
    `draft:proposal:${resource.id}`,
    {
      contact: { name: '', email: '', phone: '' },
      organizationName: '',
      purpose: '',
      bookingDate: '',
      startMinute: '',
      durationMinutes: '',
      attendeeCount: '',
    },
  );
  // The calendar always shows a day, so the form always holds one: the first
  // open day from today, unless the draft kept a day that is still to come.
  const closed = useMemo(() => closedWeekdays(resource.operatingHours), [resource.operatingHours]);
  const form = useZodForm(schema, {
    defaultValues: {
      ...draft,
      bookingDate: draft.bookingDate >= today ? draft.bookingDate : firstOpenDay(today, closed),
    },
  });
  useEffect(() => {
    const subscription = form.watch((values, { name }) => {
      // One person per seat: a count over the room's seats (typed or pasted)
      // drops to the most it holds, rather than waiting for Send to refuse it.
      if (name === 'attendeeCount' && Number(values.attendeeCount) > resource.capacity) {
        form.setValue('attendeeCount', String(resource.capacity), { shouldValidate: true });
        return;
      }
      setDraft(values as ProposalForm);
    });
    return () => subscription.unsubscribe();
  }, [form, setDraft, resource.capacity]);

  // The calendar is one control for three form values: the day, and the run
  // picked on it as a start and a length.
  const date = useController({ control: form.control, name: 'bookingDate' });
  const start = useController({ control: form.control, name: 'startMinute' });
  const duration = useController({ control: form.control, name: 'durationMinutes' });

  // The picked day's opening hours and free hours, from the server. Fresh for
  // every day picked, keeping the last board on screen until the next lands.
  const availability = useQuery({
    ...spaceQueries.availability(resource.id, date.field.value),
    enabled: date.field.value >= today,
    placeholderData: keepPreviousData,
  });
  const slots = availability.data?.slots;

  // A space sold as a block (one price for the afternoon) is taken whole, so
  // its hourly board folds into one slot over the day's window, free only if
  // every hour in it is. A closed day keeps its empty board.
  const isBlock = resource.rate.unit === 'block';
  const boardSlots = useMemo(() => {
    const data = availability.data;
    if (!data || !isBlock || data.slots.length === 0) return data?.slots ?? [];
    if (data.openFromMinute === null || data.openToMinute === null) return [];
    return [
      {
        startMinute: data.openFromMinute,
        endMinute: data.openToMinute,
        available: data.slots.every((s) => s.available),
      },
    ];
  }, [availability.data, isBlock]);

  const startValue = start.field.value === '' ? null : Number(start.field.value);
  const length = Number(duration.field.value);
  const selection: Selection | null =
    startValue === null || !length ? null : { from: startValue, to: startValue + length };

  const select = (next: Selection | null) => {
    form.setValue('startMinute', next ? String(next.from) : '', { shouldValidate: !!next });
    form.setValue('durationMinutes', next ? String(next.to - next.from) : '', {
      shouldValidate: !!next,
    });
  };

  // A block is the day's only choice, so it is picked as soon as the day's
  // board lands, if it is free. Not on the last day's board still on screen
  // while the new one loads, and not over a run already picked.
  const block = boardSlots[0];
  useEffect(() => {
    if (!isBlock || availability.isPlaceholderData || !block?.available || selection) return;
    select({ from: block.startMinute, to: block.endMinute });
  });

  // A draft restored from an earlier visit can hold hours that have since been
  // taken; clear them rather than show a run the board no longer has free.
  useEffect(() => {
    if (!slots || startValue === null || !length) return;
    const free = new Set(slots.filter((s) => s.available).map((s) => s.startMinute));
    if (!fits(free, startValue, length)) {
      form.setValue('startMinute', '');
      form.setValue('durationMinutes', '');
    }
  }, [slots, startValue, length, form]);

  // The server prices it; this is only so the guest is not surprised. A block
  // is one flat price whatever the length.
  const estimate =
    resource.rate.unit === 'hour'
      ? resource.rate.amountMinor * (length / 60)
      : resource.rate.amountMinor;
  const timeError = availability.isError
    ? undefined
    : (date.fieldState.error ?? start.fieldState.error ?? duration.fieldState.error)?.message;

  // A retry of the same body reuses its key, so a lost response cannot file
  // the proposal twice; any edit gets a fresh one.
  const idempotency = useIdempotencyKey(`idem:proposal:${resource.id}`);
  /** A failure no field can show. `null` once dismissed or a new send starts. */
  const [formError, setFormError] = useState<unknown>(null);

  /** The time the visitor picked is no longer good: say so there, and read the hours again. */
  const timeRefused = (message: string) => {
    form.setError('startMinute', { message }, { shouldFocus: true });
    void availability.refetch();
  };

  const submitting = useMutation({
    mutationFn: proposalsApi.submitProposal,
    onMutate: () => setFormError(null),
    onSuccess: (receipt) => {
      // Filed: nothing left to retry, and nothing to restore.
      idempotency.reset();
      clearDraft();
      onReceipt(receipt);
    },
    onError: (error) => {
      // Field mistakes the form's own checks did not catch land under the field.
      if (applyServerErrors(error, form.setError)) return;

      const code = error instanceof ApiError ? error.code : undefined;
      if (code === 'PROPOSAL_CAPACITY_EXCEEDED') {
        const details = (error as ApiError).body?.details as { capacity?: number } | undefined;
        form.setError(
          'attendeeCount',
          {
            message: `${resource.name} holds up to ${details?.capacity ?? resource.capacity} people.`,
          },
          { shouldFocus: true },
        );
        return;
      }
      if (code === 'BOOKING_CONFLICT')
        return timeRefused('That time was just taken. Pick another start.');
      if (code === 'BOOKING_OUTSIDE_OPERATING_HOURS')
        return timeRefused(`${resource.name} is not open then. Pick another time.`);
      if (code === 'BOOKING_TIME_PAST')
        return timeRefused('That time has passed. Pick a later one.');
      if (code === 'PROPOSAL_REQUEST_ID_REUSED') {
        // The key went out earlier with different details. A new one is made
        // on the next press; the visitor only has to press again.
        idempotency.reset();
      }
      setFormError(error);
    },
  });

  return (
    <Form
      form={form}
      className="mt-block flex flex-col gap-card"
      onSubmit={(values) => {
        const body = {
          resourceId: resource.id,
          contact: values.contact,
          ...(values.organizationName ? { organizationName: values.organizationName } : {}),
          purpose: values.purpose,
          bookingDate: values.bookingDate,
          startMinute: values.startMinute,
          durationMinutes: values.durationMinutes,
          attendeeCount: values.attendeeCount,
        };
        submitting.mutate({ requestId: idempotency.keyFor(body), ...body });
      }}
    >
      {/* When first, then what, then who: the step that can fail (nothing
          free that day) comes before anything is typed. The same
          month-and-hours calendar the meeting room is booked on. */}
      <FormSection
        title="Date and time"
        description={selection && !timeError ? `Estimated ${formatCents(estimate)}` : undefined}
        className="gap-4"
      >
        {availability.isPending ? (
          <LoadingBlock />
        ) : availability.isError ? (
          <ErrorBlock
            title="We couldn't load the free hours"
            error={availability.error}
            onRetry={() => void availability.refetch()}
          />
        ) : (
          <BookingCalendar
            size="compact"
            disablePast
            closedWeekdays={closed}
            block={isBlock}
            resource={{
              name: resource.name,
              minimumBookingMinutes: resource.minimumBookingMinutes,
            }}
            date={date.field.value}
            onDateChange={(next) => {
              date.field.onChange(next);
              // Hours are per day, so a run picked on another day means nothing here.
              select(null);
            }}
            slots={boardSlots}
            openFromMinute={availability.data.openFromMinute}
            openToMinute={availability.data.openToMinute}
            selection={selection}
            onSelect={select}
            showSummary
          />
        )}
        {timeError && (
          <p role="alert" className="flex items-start gap-1.5 text-xs text-danger-text">
            <ErrorMark className="size-3.5 shrink-0 translate-y-0.5" aria-hidden />
            {timeError}
          </p>
        )}
      </FormSection>

      <FormSection title="Your booking">
        <div className="grid gap-5 sm:grid-cols-2">
          <Field
            name="attendeeCount"
            label="Number of attendees"
            required
            hint={`Up to ${resource.capacity}, one per seat.`}
          >
            <Input
              type="number"
              inputMode="numeric"
              min={1}
              max={resource.capacity}
              step={1}
              // People come whole: a number field otherwise takes a decimal
              // point, an exponent and a sign.
              onKeyDown={(e) => {
                if (['.', ',', 'e', 'E', '+', '-'].includes(e.key)) e.preventDefault();
              }}
            />
          </Field>
          <Field name="purpose" label="What is it for?" required className="sm:col-span-2">
            <Textarea rows={4} />
          </Field>
        </div>
      </FormSection>

      <FormSection title="Contact details">
        <div className="grid gap-5 sm:grid-cols-2">
          <Field name="contact.name" label="Full name" required>
            <Input autoComplete="name" />
          </Field>
          <Field name="organizationName" label="Organization">
            <Input autoComplete="organization" />
          </Field>
          <Field
            name="contact.email"
            label="Email"
            required
            hint="Every update about it is sent here."
          >
            <Input type="email" autoComplete="email" />
          </Field>
          <Field name="contact.phone" label="Phone / WhatsApp" required>
            <PhoneInput />
          </Field>
        </div>
      </FormSection>

      <Alert tone="neutral" title="Nothing is held yet">
        MDS reviews your proposal and emails you a price. The date is yours once the 50% deposit or
        the full amount is paid at MDS.
      </Alert>

      {formError !== null && (
        <ErrorBlock
          title="We couldn't send your proposal"
          error={formError}
          message={
            isReusedKey(formError)
              ? 'Your details changed since the last try. Press Send again.'
              : undefined
          }
          onDismiss={() => setFormError(null)}
        />
      )}

      <div className="flex flex-wrap justify-center gap-3">
        <Button type="submit" disabled={submitting.isPending}>
          {submitting.isPending ? 'Sending…' : 'Send proposal'}
        </Button>
        <Button asChild variant="outline">
          <Link to={`/spaces/${spaceSlug}`}>Back to the space</Link>
        </Button>
      </div>
    </Form>
  );
}

/** What happened, in place of the form — a form still sitting there invites sending it twice. */
function Receipt({ receipt }: { receipt: GuestProposalReceipt }) {
  const end = receipt.startMinute + receipt.durationMinutes;
  return (
    <OutcomePanel
      tone="success"
      title="Proposal received"
      description="MDS will review it and email you at each step. It does not hold the space until the deposit or the full amount is paid."
      details={{
        title: receipt.resource.name,
        items: [
          { icon: LuHash, marker: 'Reference', description: receipt.reference },
          { icon: LuCalendarDays, marker: 'Date', description: longDateLabel(receipt.bookingDate) },
          { icon: LuClock, marker: 'Time', description: clockRange(receipt.startMinute, end) },
          { icon: LuUsers, marker: 'Attendees', description: String(receipt.attendeeCount) },
        ],
      }}
      actions={
        <>
          <Button asChild size="lg">
            <Link to="/spaces">All spaces</Link>
          </Button>
          <Button asChild variant="outline" size="lg">
            <Link to="/">Home</Link>
          </Button>
        </>
      }
    />
  );
}
